import { useEffect, useState, useRef } from 'react'
import { imagecompoAPI } from '../lib/api'
import { useImagecompoSocket } from '../lib/imagecompoSocket'

/*
  Beamer view voor AI Image Compo.
  Views: idle | entry | pauze | reveal | results
*/
export default function ImageCompoStagePage() {
  const [data, setData] = useState(null)
  const load = () => imagecompoAPI.getState().then(r => setData(r.data)).catch(() => {})
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const bg = 'linear-gradient(180deg, var(--stage-bg) 0%, var(--stage-panel) 100%)'

  if (!data?.show) {
    return <div style={{ background: bg, color: '#eee', minHeight: '100vh' }} className="flex items-center justify-center text-2xl">Geen actieve show</div>
  }
  const state = data.state || {}
  const view = state.current_view || 'idle'

  return (
    <div style={{ background: bg, color: '#eee', minHeight: '100vh', overflow: 'hidden' }}>
      {view === 'idle' && <IdleView show={data.show} />}
      {view === 'entry' && <EntryView entry={data.current_entry} show={data.show} />}
      {view === 'pauze' && <PauzeView show={data.show} />}
      {view === 'reveal' && <RevealView data={data} kind="admin" />}
      {view === 'public_reveal' && <RevealView data={data} kind="public" />}
      {view === 'results' && <ResultsView data={data} />}
    </div>
  )
}

/* ============ IDLE ============ */
function IdleView({ show }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-10">
      <div className="text-sm uppercase tracking-widest text-purple-400 mb-4">AI Image Compo</div>
      <h1 style={{ fontFamily: "'Big Shoulders Display', Impact, sans-serif", fontWeight: 900, fontSize: 'clamp(48px, 8vw, 140px)', textTransform: 'uppercase' }}>
        {show.name}
      </h1>
      {show.edition && <div className="text-2xl text-muted-foreground mt-4">{show.edition}</div>}
    </div>
  )
}

/* ============ ENTRY (image display) ============ */
function EntryView({ entry, show }) {
  if (!entry) return <IdleView show={show} />
  return (
    <div className="min-h-screen flex flex-col p-6">
      <div className="text-center mb-4">
        <div className="text-xs uppercase tracking-widest text-purple-400">Inzending</div>
        <h2 style={{ fontFamily: "'Big Shoulders Display', Impact, sans-serif", fontWeight: 900, fontSize: 'clamp(40px, 5vw, 80px)', lineHeight: 1, textTransform: 'uppercase' }}>{entry.title}</h2>
        <div className="text-xl text-purple-300 mt-2">{entry.team_name}</div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        {entry.image_url ? (
          <img src={entry.image_url} alt="" style={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 80px rgba(139,63,255,0.5)' }} />
        ) : <div className="text-muted-foreground">Geen image</div>}
      </div>
      {entry.description && <div className="text-center text-sm text-muted-foreground mt-4 max-w-3xl mx-auto italic">"{entry.description}"</div>}
    </div>
  )
}

/* ============ PAUZE ============ */
function PauzeView({ show }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-10">
      <div className="text-sm uppercase tracking-widest text-yellow-400 mb-4 animate-pulse">— Pauze —</div>
      <h1 style={{ fontFamily: "'Big Shoulders Display', Impact, sans-serif", fontWeight: 900, fontSize: 'clamp(56px, 10vw, 180px)', textTransform: 'uppercase' }}>
        Even geduld
      </h1>
      <div className="text-xl text-muted-foreground mt-4">{show.name}</div>
    </div>
  )
}

/* ============ REVEAL ============ */
function RevealView({ data, kind }) {
  const isPub = kind === 'public'
  const current = isPub ? data.current_public_reveal : data.current_reveal
  const standings = data.standings || []
  const accent = isPub ? 'var(--stage-cold)' : 'var(--stage-purple)'
  const headerLabel = isPub ? 'Punten van het publiek' : 'Punten van de admin jury'
  const bigPoints = current ? (isPub ? current.points_public : current.points_admin) : null
  return (
    <div className="min-h-screen grid grid-cols-2 gap-6 p-6">
      <div className="flex flex-col items-center justify-center text-center">
        <div className="text-xs uppercase tracking-widest mb-4" style={{ color: accent, letterSpacing: '.24em', fontWeight: 700 }}>
          {headerLabel}
        </div>
        {current ? (
          <>
            <h1 style={{ fontFamily: "'Big Shoulders Display', Impact, sans-serif", fontWeight: 900, fontSize: 'clamp(48px, 6vw, 96px)', textTransform: 'uppercase', lineHeight: 1 }}>
              {current.team_name}
            </h1>
            {current.image_url && (
              <img src={current.image_url} alt="" style={{ maxWidth: '80%', maxHeight: '38vh', objectFit: 'contain', borderRadius: 10, marginTop: 16, boxShadow: `0 12px 40px ${accent}80` }} />
            )}
            <div style={{ fontSize: 'clamp(72px, 12vw, 200px)', fontWeight: 900, lineHeight: 1, marginTop: 16, color: 'var(--stage-gold)', textShadow: '0 0 40px rgba(255,207,64,0.7)' }}>
              {bigPoints}
            </div>
            {isPub && current.points_admin != null && (
              <div className="text-sm text-muted-foreground mt-2">
                admin {current.points_admin} + publiek {current.points_public} = <b>totaal {current.points}</b>
              </div>
            )}
          </>
        ) : (
          <div className="text-2xl text-muted-foreground">Wacht op reveal…</div>
        )}
      </div>
      <div className="flex flex-col">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Tussenstand</div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {standings.map((r, i) => (
            <div key={r.entry_id} className={`flex items-center gap-3 p-2 rounded border ${r.revealed ? 'bg-card/80' : 'border-border bg-card/40 opacity-60'}`}
                 style={r.revealed ? { borderColor: `${accent}66`, background: `${accent}12` } : {}}>
              <div className="w-8 text-center text-lg font-bold tabular-nums">{r.revealed ? i + 1 : '·'}</div>
              <div className="w-12 h-12 rounded border border-border overflow-hidden flex items-center justify-center shrink-0 bg-muted/30">
                {r.image_url ? <img src={r.image_url} alt="" className="max-w-full max-h-full object-contain" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.team_name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {r.points_admin != null && <span>admin {r.points_admin}</span>}
                  {r.points_public != null && <span> · publiek {r.points_public}</span>}
                </div>
              </div>
              <div className="text-2xl font-bold tabular-nums w-16 text-right">{r.points ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ============ RESULTS ============ */
function ResultsView({ data }) {
  const standings = (data.standings || []).filter(r => r.revealed).sort((a, b) => b.points - a.points)
  const winner = standings[0]
  const podium = standings.slice(0, 3)
  return (
    <div className="min-h-screen flex flex-col p-8 relative">
      <ConfettiOverlay />
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-widest text-purple-400">Winnaar</div>
        <h1 style={{ fontFamily: "'Big Shoulders Display', Impact, sans-serif", fontWeight: 900, fontSize: 'clamp(60px, 9vw, 160px)', textTransform: 'uppercase', color: 'var(--stage-gold)', textShadow: '0 0 50px rgba(255,207,64,0.6)' }}>
          {winner?.team_name || '—'}
        </h1>
        {winner?.title && <div className="text-2xl text-muted-foreground mt-2">"{winner.title}"</div>}
      </div>
      <div className="grid grid-cols-3 gap-6 flex-1 items-end">
        {[podium[1], podium[0], podium[2]].map((p, i) => {
          if (!p) return <div key={i} />
          const pos = [2, 1, 3][i]
          const height = pos === 1 ? '80%' : pos === 2 ? '65%' : '50%'
          const bg = pos === 1 ? 'var(--stage-gold)' : pos === 2 ? '#c0c0c0' : '#cd7f32'
          return (
            <div key={p.entry_id} className="flex flex-col items-center">
              {p.image_url && <img src={p.image_url} alt="" style={{ width: '100%', maxHeight: '30vh', objectFit: 'contain', marginBottom: 12, borderRadius: 8, boxShadow: `0 12px 40px ${bg}66` }} />}
              <div className="text-xl font-bold text-center">{p.team_name}</div>
              <div className="text-3xl font-bold tabular-nums mt-1" style={{ color: bg }}>{p.points}</div>
              <div style={{ width: '100%', height, background: bg, marginTop: 12, borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 20, color: '#000', fontSize: 48, fontWeight: 900 }}>
                {pos}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ============ CONFETTI ============ */
function ConfettiOverlay() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const fit = () => {
      const r = canvas.getBoundingClientRect()
      canvas.width = Math.max(2, Math.floor(r.width * dpr))
      canvas.height = Math.max(2, Math.floor(r.height * dpr))
    }
    fit()
    const ro = new ResizeObserver(fit); ro.observe(canvas)
    const COLORS = ['var(--stage-hot)','var(--stage-cold)','var(--stage-gold)','var(--stage-purple)','var(--stage-lime)','#ffffff']
    const parts = []
    for (let i = 0; i < 180; i++) {
      parts.push({
        x: Math.random() * canvas.width,
        y: -Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 60 * dpr,
        vy: (60 + Math.random() * 120) * dpr,
        size: (3 + Math.random() * 4) * dpr,
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 6,
        color: COLORS[(Math.random() * COLORS.length) | 0],
      })
    }
    let last = performance.now(), raf
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (const p of parts) {
        p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vrot * dt
        if (p.y > canvas.height + 20) {
          p.y = -20; p.x = Math.random() * canvas.width
        }
        ctx.save()
        ctx.translate(p.x, p.y); ctx.rotate(p.rot)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size)
        ctx.restore()
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 50, width: '100vw', height: '100vh' }} />
}
